/**
 * The time zones the product accepts: Brazil only. Organizations and units pick one of these;
 * the API refuses anything else.
 *
 * Brazil has had no daylight saving time since 2019, so several IANA zones of the country are
 * the same clock today. The list keeps one entry per distinct clock a customer would
 * recognize, each pointing to the canonical IANA zone of that region:
 *
 * - `America/Noronha` (UTC-02:00): Fernando de Noronha.
 * - `America/Sao_Paulo` (UTC-03:00): the official time of Brasília — South, Southeast,
 *   Goiás and the Federal District.
 * - `America/Fortaleza` (UTC-03:00): the North and Northeast on Brasília time (Belém,
 *   Fortaleza, Recife, Salvador, Maceió, Palmas…). It stands for `America/Belem`,
 *   `America/Recife`, `America/Bahia`, `America/Maceio`, `America/Araguaina` and
 *   `America/Santarem`, which differ only in their past daylight saving rules.
 * - `America/Manaus` (UTC-04:00): Amazonas.
 * - `America/Cuiaba` (UTC-04:00): Mato Grosso and Mato Grosso do Sul (stands for
 *   `America/Campo_Grande`).
 * - `America/Porto_Velho` (UTC-04:00): Rondônia.
 * - `America/Boa_Vista` (UTC-04:00): Roraima.
 * - `America/Rio_Branco` (UTC-05:00): Acre and the southwest of Amazonas (stands for
 *   `America/Eirunepe`).
 *
 * Every zone is still a real IANA id, so date libraries and the database convert with it.
 */
export const BRAZILIAN_TIME_ZONES = [
  { id: 'America/Noronha', utcOffset: '-02:00' },
  { id: 'America/Sao_Paulo', utcOffset: '-03:00' },
  { id: 'America/Fortaleza', utcOffset: '-03:00' },
  { id: 'America/Manaus', utcOffset: '-04:00' },
  { id: 'America/Cuiaba', utcOffset: '-04:00' },
  { id: 'America/Porto_Velho', utcOffset: '-04:00' },
  { id: 'America/Boa_Vista', utcOffset: '-04:00' },
  { id: 'America/Rio_Branco', utcOffset: '-05:00' },
] as const;

export type BrazilianTimeZone = (typeof BRAZILIAN_TIME_ZONES)[number]['id'];

/** The time zone of a new organization: the official time of Brasília. */
export const DEFAULT_TIME_ZONE: BrazilianTimeZone = 'America/Sao_Paulo';

const IDS: ReadonlySet<string> = new Set(BRAZILIAN_TIME_ZONES.map((zone) => zone.id));

/** Whether `value` is one of the accepted time zones. */
export function isBrazilianTimeZone(value: string): value is BrazilianTimeZone {
  return IDS.has(value);
}
